// © Microsoft Corporation. All rights reserved.

import { DesignSystem, isDarkMode, neutralFillRest, neutralForegroundHint, neutralForegroundRest, neutralLayerCard, neutralOutlineActive } from "@microsoft/fast-components-styles-msft";
import { enums, profileHrColor, reset } from "@msnews/social-utils";

import { applyLocalizedProperty } from "@microsoft/fast-jss-utilities";

const fontSize = "13px";
const borderRadius = "2px";

export const CopyrightFormStyles = (config: DesignSystem) => ({
    copyrightFormContainer: {
        ...reset,
        "box-sizing": "border-box",
        width: "500px",
        "max-width": "100vw",
        display: "flex !important",
        "flex-direction": "column",
        padding: "19px",
        "padding-bottom": "20px",
        background: neutralLayerCard,
        "max-height": "85vh",
        "min-height": "550px",
        "overflow-y": "auto",
        "font-size": fontSize,

        "& .cwt-input-container": {
            display: "flex",
            "flex-direction": "column"
        },

        "& .cwt-h-separator": {
            background: profileHrColor,
            height: "0px",
            border: "1px solid",
            "border-color": neutralOutlineActive,
            "box-sizing": "border-box",
            width: "100%",
            margin: "20px 0"
        },
        "& .cwt-sub-text": {
            "font-weight": "normal",
            "margin-top": "8px",
            "margin-bottom": "12px",
            color: isDarkMode(config) ? "#D9D9D9" : neutralForegroundHint
        },
        "& .cwt-title": {
            display: "flex",
            "flex-direction": "column",
            "align-items": "center",
            "& .cwt-main-title": {
                margin: "16px 16px 8px",
                "font-weight": "600",
                "font-size": "24px",
                color: neutralForegroundRest
            }
        },
        "& div.cwt-buttons": {
            display: "flex",
            "flex-wrap": "wrap",
            "justify-content": "center",
            margin: "20px 0 0",

            "& .cwt-fast-button": {
                "min-width": "134px",
                "margin-bottom": "11px",
                [applyLocalizedProperty("margin-right", "margin-left", config.direction)]: "8px"
            }
        },
        "& .cwt-input-label": {
            "font-weight": "600",
            margin: "20px 2px 8px",
            color: neutralForegroundRest,
            display: "flex",
            "justify-content": "space-between",

            "& .cwt-input-label-asterisk": {
                color: isDarkMode(config) ? "#FFCCCC" : "#B30000",
                "&::before": {
                    content: "'*'"
                }
            }
        },
        "& .cwt-required-text": {
            color: isDarkMode(config) ? "#FFCCCC" : "#B30000"
        },
        "& .cwt-input-box": {
            background: neutralLayerCard,
            border: "1px solid",
            "border-color": neutralOutlineActive,
            "box-shadow": "0px 4px 4px rgba(0, 0, 0, 0.02)",
            "border-radius": borderRadius,
            color: isDarkMode(config) ? "white" : "black",
            padding: "9px"
        },
        "& .cwt-input-disabled": {
            background: neutralFillRest,
            "font-weight": "400",
            color: neutralForegroundRest
        },
        "& .cwt-input-box-error": {
            "border-color": isDarkMode(config) ? "#FFCCCC" : "#B30000",
            "box-shadow": isDarkMode(config) ? "0px 1px 1px #FFCCCC" : "0px 1px 1px #B30000"
        },
        "& .cwt-header-sections": {
            "margin-bottom": "8px",
            "font-size": `calc( ${fontSize} + (2px))`,
            color: neutralForegroundRest,
            "font-weight": "600"
        },
        "& .cwt-header-comment-id": {
            "margin-top": "20px",
            "margin-bottom": "10px",
            "font-weight": "600",
            color: isDarkMode(config) ? "#D9D9D9" : neutralForegroundHint
        },

        "& .cwt-media-container": {
            position: "relative",
            "min-height": "36px",
            display: "flex",
            "flex-wrap": "wrap",
            "align-items": "center",
            border: "1px solid",
            "border-color": neutralOutlineActive,
            padding: "4px",
            "box-shadow": "0px 4px 4px rgba(0, 0, 0, 0.02)",
            "& .cwt-media-input": {
                position: "absolute",
                height: "100%",
                opacity: "0",
                width: "100%",
                "&:hover": {
                    cursor: "pointer"
                }
            },
            "& .cwt-fast-button": {
                "min-width": "134px",
                margin: "4px 0px 4px",
                "z-index": "1",
                [applyLocalizedProperty("margin-right", "margin-left", config.direction)]: "12px"
            },
            "& .cwt-media": {
                margin: "4px 0px 4px",
                [applyLocalizedProperty("margin-right", "margin-left", config.direction)]: "12px",
                display: "flex",
                "z-index": "10",
                "align-items": "center",
                [applyLocalizedProperty("padding-left", "padding-right", config.direction)]: "10px",
                "border-radius": "4px",
                "background-color": isDarkMode(config) ? "#4D4D4D" : "#C2C2C2",
                color: neutralForegroundRest,
                "&.cwt-upload-failed": {
                    "background-color": isDarkMode(config) ? "#FF493E" : "#FF7970"
                },
                "&.cwt-upload-succeed": {
                    "background-color": isDarkMode(config) ? "#576F3C" : "#A4BE87"
                }
            },
            "& .cwt-media-status": {
                "font-style": "italic"
            },
            "& .cwt-media-span": {
                "z-index": "10",
                color: isDarkMode(config) ? "#D9D9D9" : neutralForegroundHint
            }
        },
        ["&." + enums.responsiveLevels.L0]: {
            width: "100vw",
            "max-width": "100vw",
            "max-height": "85vh",
            "& .cwt-input-label": {
                "flex-direction": "column",
                "cwt-required-text": {
                    "margin-top": "10px"
                }
            }
        }
    }
});
